/* eslint-disable @typescript-eslint/no-empty-function */
import React, { MouseEvent, useCallback, useEffect, useState } from 'react'
import KnowService from 'services/KnowService'
import { Know as KnowInterface, KnowFormat } from 'interfaces/Know'
import { onPageEnter } from 'lib/tracking/pageEnter'
import { trackPromise } from 'react-promise-tracker'
import { ApiError } from 'services/ApiService'
import { toast } from 'react-toastify'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import Router, { useRouter } from 'next/router'
import Meta from 'components/modules/SEO/Meta'
import { AnalyticsEventKnowOwnView } from 'lib/constants/AnalyticsEventType'
import { getReferrerScreen, parseUuidFromSlug } from 'lib/formatting/url'
import { trackEvent } from 'lib/tracking/analytics-service'
import { appFeedsRoute, knowViewRoute, signInRoute } from 'lib/constants/routes'
import _useTranslation from 'lib/hooks/_useTranslation'
import { parseLocale } from 'lib/constants/localization'
import DesktopKnowSection from 'components/feature/Know/view/Authenticated/DesktopKnowSection'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { Flashcard } from 'interfaces/Flashcard'
import MobileKnowSection from 'components/feature/Know/view/Authenticated/MobileKnowSection'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'

const loadingAreas = {
    know: 'know',
}

function KnowDisplayWithoutExperiment() {
    const [knowUuid, setKnowUuid] = useState<string | null>(null)
    const [knowSeoInfo, setKnowSeoInfo] = useState<KnowSeoInfo | null>(null)
    const [similarKnows, setSimilarKnows] = useState<KnowInterface[] | null>(null)
    const [flashcards, setFlashcards] = useState<Flashcard[] | null>(null)
    const router = useRouter()
    const { locale } = router
    const parsedLocale = parseLocale(locale)
    const { slug } = router.query
    const { know } = knowSeoInfo || {}

    const authenticatedUser = useSelector((state: AppState) => state.user)
    const authenticatedKnower = useSelector((state: AppState) => state.knower)
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')
    const referrerScreen = getReferrerScreen(router)

    const isOwner = !!(authenticatedUser && know?.knower.uuid === authenticatedKnower?.uuid)

    useEffect(() => {
        onPageEnter()
    }, [])

    useEffect(() => {
        if (!isOwner) {
            router.push(appFeedsRoute)
        }
    }, [isOwner, router])

    const fetchData = useCallback(
        (identifier: string) => {
            trackPromise(
                KnowService.get(identifier)
                    .then((know) => {
                        const localKnowSeoInfo: KnowSeoInfo = {
                            know: know,
                            title: know.title,
                            description: know.description,
                            experimentVariants: [],
                            transcript: null,
                            summary: null,
                            seoPages: null,
                            questions: null,
                            topicsPath: [],
                            isEnhancedTranscript: false,
                            similarKnows: [],
                        }
                        setKnowSeoInfo(localKnowSeoInfo)
                        setKnowUuid(know.uuid)
                        trackEvent(AnalyticsEventKnowOwnView, {
                            know,
                            referrerScreen,
                        })

                        if (know.knowFormat === KnowFormat.Flashcards) {
                            KnowService.getFlashcards(know.uuid)
                                .then((data) => {
                                    data?.length > 0 && setFlashcards(data)
                                })
                                .catch((error) => {
                                    throw error
                                })
                        }

                        KnowService.listSimilarKnowsViaAlgorithm(know.uuid)
                            .then((data) => {
                                data?.length > 0 && setSimilarKnows(data)
                            })
                            .catch((error) => {
                                throw error
                            })

                        const path = knowViewRoute(know.slug)

                        if (Router.asPath !== path) {
                            Router.replace(path)
                        }
                    })
                    .catch((error) => {
                        if (error instanceof ApiError) {
                            if (error.type === ErrorTypes.NotFound) {
                                toast.error(t('knows/knowNotFoundError'))
                            } else if (error.type === ErrorTypes.ForbiddenError) {
                                Router.push(signInRoute({ redirect: Router.asPath }))
                            } else {
                                error.handleUnknownError(tCommon, 'know.get')
                            }
                        } else {
                            throw error
                        }
                    }),
                loadingAreas.know
            )
        },
        [parsedLocale.countryCode, referrerScreen, t, tCommon]
    )

    useEffect(() => {
        if (typeof slug !== 'string') {
            return
        }

        const knowIdentifier = parseUuidFromSlug(slug)
        if (knowIdentifier === null) {
            return
        }

        setKnowUuid(knowIdentifier)

        fetchData(knowIdentifier)
    }, [fetchData, referrerScreen, slug])

    if (!knowUuid || !know) return null

    const onContextMenu = (event: MouseEvent<HTMLDivElement>) => {
        // event.preventDefault()
        // event.stopPropagation()
        // return false
    }

    return (
        <div onContextMenu={onContextMenu}>
            {/* <DisableScreenShotAndCopy /> */}
            <Meta
                title={`${know?.title} (${know?.subjects.map((s) => s.name).join(', ')})`}
                description={t('knows/slugDefaultPageDescription')}
                noIndex
                imageUrl={know?.thumbnailSmallUrl || undefined}
                canonical
            />
            <DesktopKnowSection
                similarKnows={similarKnows}
                knowSeoInfo={knowSeoInfo}
                loadingArea={loadingAreas.know}
                isOwner={!!isOwner}
                flashcards={flashcards}
            />
            <MobileKnowSection
                similarKnows={similarKnows}
                knowSeoInfo={knowSeoInfo}
                flashcards={flashcards}
                transcript={null}
                isOwner={!!isOwner}
                disableConversion
            />
        </div>
    )
}

export default KnowDisplayWithoutExperiment
